import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

/**
 * Shared pricing helper for the Nigeria market (frontend display only).
 *
 * Customer-facing landed price model:
 *   priceNGN = (usdLandedCost + SHIPPING_USD) * rate
 *
 * usdLandedCost is the car's C_Price (Nigerian landed cost in USD); SHIPPING_USD is a
 * flat shipping fee added AFTER taxes (not taxed). The USD->NGN rate is fetched live
 * from a free, CORS-enabled, no-key FX feed (jsdelivr CDN), with a documented fallback
 * host and a static fallback rate if both are unreachable.
 */
@Injectable({ providedIn: 'root' })
export class NigeriaPriceService {
  // Flat shipping fee (USD), added after taxes — not taxed.
  static readonly SHIPPING_USD = 2500;

  // Static fallback USD->NGN rate, used only if the live fetch fails.
  static readonly FALLBACK_USD_TO_NGN = 1360;

  // Free, CORS-enabled, no-key USD rate feed (fawazahmed0/currency-api) + documented fallback host.
  private static readonly RATE_URLS = [
    'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.json',
    'https://latest.currency-api.pages.dev/v1/currencies/usd.json',
  ];

  readonly shippingUsd = NigeriaPriceService.SHIPPING_USD;
  rate = NigeriaPriceService.FALLBACK_USD_TO_NGN;

  constructor(private http: HttpClient) {
    this.fetchRate(0);
  }

  // Try each feed in order; keep the fallback rate if all fail.
  private fetchRate(i: number): void {
    if (i >= NigeriaPriceService.RATE_URLS.length) { return; }
    this.http.get<any>(NigeriaPriceService.RATE_URLS[i]).subscribe(
      (res) => {
        const ngn = res?.usd?.ngn;
        const fb = NigeriaPriceService.FALLBACK_USD_TO_NGN;
        // Accept only a finite number within a plausibility band around the fallback,
        // so a malformed or compromised feed cannot drive customer-facing prices.
        if (typeof ngn === 'number' && Number.isFinite(ngn) && ngn >= 0.5 * fb && ngn <= 2 * fb) {
          this.rate = ngn;
        } else {
          this.fetchRate(i + 1);
        }
      },
      () => { this.fetchRate(i + 1); }
    );
  }

  /**
   * Convert a USD landed cost (car C_Price) to a customer-facing NGN price,
   * including the flat shipping fee. Returns a whole-number NGN amount.
   */
  landedNgn(usdLanded: number): number {
    // Convert an already-landed USD value (e.g. a slider threshold) to NGN.
    return Math.round((usdLanded || 0) * this.rate);
  }

  // Full Nigerian landed cost (USD) computed client-side from the source price (FOB = CarPrice).
  // Shipping is part of the taxable CIF: CIF = FOB + SHIPPING_USD.
  landedUsd(fob: number): number {
    const f = (typeof fob === 'number' && fob > 0) ? fob : 0;
    if (f <= 0) { return 0; }
    const cif = f + NigeriaPriceService.SHIPPING_USD;
    const duty = 0.20 * cif;
    const surcharge = 0.07 * duty;
    const fcs = 0.04 * f;
    const etls = 0.005 * cif;
    const lvy = 0.05 * cif;
    const vatBase = cif + duty + surcharge + fcs + etls + lvy;
    const vat = 0.075 * vatBase;
    return cif + duty + surcharge + fcs + etls + lvy + vat;
  }

  // Customer-facing NGN price computed client-side from the source price (same approach as the UAE page).
  landedNgnFromFob(fob: number): number {
    return Math.round(this.landedUsd(fob) * this.rate);
  }
}
